// ─── Gallery + Support Our Project editor modals ──────────────────────────────
//
//   GalleryEditorModal        — "Change Gallery": single album dropdown.
//                               User picks one of their project's albums and
//                               the widget shows photos from that album. The
//                               widget's View All button deep-links back to
//                               this album via DashboardContext.goToGallery.
//
//   SupportProjectEditorModal — Two-column "Support Our Project Settings":
//                                 Left  — Donate Link + sponsor row list
//                                         (Edit/Delete per row, Add Sponsor)
//                                 Right — live preview of the widget +
//                                         drag-reorder of sponsor logos
//
// Both editors persist via the caller's onSave callback — widgets store the
// settings to localStorage (the channel every other configurable widget in
// DashboardWidgets.jsx uses).
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from 'react'
import { X, Plus, Trash2, GripVertical, Upload } from 'lucide-react'
import {
    DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core'
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates,
    useSortable, verticalListSortingStrategy, rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const API_URL = import.meta.env.VITE_API_URL || ''
const JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'

function authHeaders() {
    const token = localStorage.getItem(JWT_KEY)
    return token ? { Authorization: `Bearer ${token}` } : {}
}

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader()
        r.onload = (e) => resolve(e.target.result)
        r.onerror = reject
        r.readAsDataURL(file)
    })
}

// ──────────────────────────────────────────────────────────────────────────
// Gallery: "Change Gallery" (single album dropdown)
// ──────────────────────────────────────────────────────────────────────────
export function GalleryEditorModal({ open, onClose, onSave, projectId, initialAlbumId = null }) {
    const [albums, setAlbums] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [selectedId, setSelectedId] = useState(initialAlbumId)

    useEffect(() => {
        if (!open) return
        setSelectedId(initialAlbumId)
        if (!projectId) { setAlbums([]); return }
        setLoading(true); setError(null)
        fetch(`${API_URL}/api/photo/album/?project=${projectId}`, { headers: authHeaders() })
            .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then((data) => {
                const list = Array.isArray(data) ? data : (data.results || [])
                setAlbums(list)
            })
            .catch((e) => { console.error('[GalleryEditor] albums:', e); setError('Could not load albums.') })
            .finally(() => setLoading(false))
    }, [open, projectId, initialAlbumId])

    useEffect(() => {
        if (!open) return
        const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open, onClose])

    if (!open) return null

    const handleSelect = () => {
        const album = albums.find((a) => String(a.id) === String(selectedId))
        if (!album) return
        onSave({ albumId: album.id, albumName: album.name || `Album ${album.id}` })
        onClose()
    }

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-gray-100">
                    <h2 className="text-base font-semibold text-[#4D4D4D]">Change Gallery</h2>
                </div>
                <div className="px-6 py-5 space-y-3">
                    {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</p>}
                    <select
                        value={selectedId || ''}
                        onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}
                        disabled={loading || albums.length === 0}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50 disabled:text-gray-400"
                    >
                        <option value="">
                            {loading ? 'Loading albums…' : (albums.length === 0 ? 'No albums available' : 'Select Album')}
                        </option>
                        {albums.map((a) => (
                            <option key={a.id} value={a.id}>{a.name || `Album ${a.id}`}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={handleSelect}
                        disabled={!selectedId || loading}
                        className="w-full bg-[#358ECD] text-white font-semibold uppercase text-sm tracking-wide py-2.5 rounded-lg hover:bg-[#2d7ab8] disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                        Select Album
                    </button>
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-sm text-[#358ECD] hover:underline"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

// ──────────────────────────────────────────────────────────────────────────
// Support Our Project: 2-column "Settings" with live preview
// ──────────────────────────────────────────────────────────────────────────

// Draggable preview tile (small)
function SortableLogoTile({ id, sponsor }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }
    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="flex items-center justify-center bg-white border border-gray-100 rounded p-2 cursor-grab active:cursor-grabbing"
            title={sponsor.name || 'Sponsor'}
        >
            <img
                src={sponsor.logo}
                alt={sponsor.name || 'Sponsor'}
                className="max-h-16 max-w-full object-contain"
                onError={(e) => { e.target.style.opacity = 0.3 }}
                draggable="false"
            />
        </div>
    )
}

// Inline edit form for a single sponsor — shown when Edit is clicked
function SponsorEditForm({ sponsor, onChange, onDone, onUpload }) {
    const fileRef = useRef(null)
    return (
        <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-2">
            <input
                type="text" placeholder="Sponsor name"
                value={sponsor.name || ''}
                onChange={(e) => onChange({ ...sponsor, name: e.target.value })}
                className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:border-[#358ECD]"
            />
            <div className="flex gap-2">
                <input
                    type="text" placeholder="Logo URL"
                    value={sponsor.logo || ''}
                    onChange={(e) => onChange({ ...sponsor, logo: e.target.value })}
                    className="flex-1 text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:border-[#358ECD]"
                />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="px-2 py-1.5 text-xs text-[#358ECD] border border-[#358ECD] rounded hover:bg-[#E8F1F8] flex items-center gap-1"
                >
                    <Upload size={12} /> Upload
                </button>
                <input
                    ref={fileRef} type="file" accept="image/*" className="hidden"
                    onChange={async (e) => {
                        const f = e.target.files?.[0]
                        if (!f) return
                        const dataUrl = await readFileAsDataUrl(f)
                        onUpload(dataUrl, f.name.replace(/\.[^.]+$/, ''))
                        e.target.value = ''
                    }}
                />
            </div>
            <input
                type="url" placeholder="Domain link (https://…)"
                value={sponsor.link || ''}
                onChange={(e) => onChange({ ...sponsor, link: e.target.value })}
                className="w-full text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:border-[#358ECD]"
            />
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={onDone}
                    className="text-xs text-[#358ECD] hover:underline font-medium"
                >
                    Done
                </button>
            </div>
        </div>
    )
}

export function SupportProjectEditorModal({ open, onClose, onSave, initialDonateUrl = '', initialSponsors = [] }) {
    const [donateUrl, setDonateUrl] = useState(initialDonateUrl)
    const [sponsors, setSponsors] = useState(initialSponsors)
    const [editingId, setEditingId] = useState(null)

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    )

    useEffect(() => {
        if (!open) return
        setDonateUrl(initialDonateUrl)
        setSponsors(initialSponsors)
        setEditingId(null)
    }, [open, initialDonateUrl, initialSponsors])

    useEffect(() => {
        if (!open) return
        const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open, onClose])

    if (!open) return null

    const updateSponsor = (id, next) => setSponsors((prev) => prev.map((s) => (s.id === id ? next : s)))
    const removeSponsor = (id) => {
        setSponsors((prev) => prev.filter((s) => s.id !== id))
        if (editingId === id) setEditingId(null)
    }
    const addSponsor = () => {
        const id = 's' + Date.now()
        setSponsors((prev) => [...prev, { id, name: '', logo: '', link: '' }])
        setEditingId(id)
    }

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return
        setSponsors((prev) => {
            const from = prev.findIndex((s) => s.id === active.id)
            const to = prev.findIndex((s) => s.id === over.id)
            return arrayMove(prev, from, to)
        })
    }

    const save = () => {
        // Drop any sponsors that ended up with no logo (e.g. user opened Add
        // Sponsor but never filled in anything).
        const cleaned = sponsors.filter((s) => s.logo && String(s.logo).trim())
        onSave({ donateUrl: donateUrl.trim(), sponsors: cleaned })
        onClose()
    }

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header with Save / Cancel buttons on the right */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
                    <h2 className="text-base font-semibold text-[#4D4D4D]">Support Our Project Settings</h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={save}
                            className="px-4 py-1.5 text-sm text-white bg-[#358ECD] rounded-lg hover:bg-[#2d7ab8] transition font-medium"
                        >Save</button>
                        <button
                            onClick={onClose}
                            className="px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                        >Cancel</button>
                    </div>
                </div>

                {/* Two-column body */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-4 overflow-y-auto flex-1">

                    {/* LEFT: Donate Link + Sponsors list */}
                    <div className="space-y-4 min-w-0">
                        <div>
                            <h3 className="text-sm font-semibold text-[#4D4D4D] mb-2">Donate Link</h3>
                            <input
                                type="url"
                                placeholder="Link"
                                value={donateUrl}
                                onChange={(e) => setDonateUrl(e.target.value)}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]"
                            />
                        </div>

                        <div>
                            <h3 className="text-sm font-semibold text-[#4D4D4D] mb-2">Sponsors</h3>
                            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 min-h-[280px]">
                                {sponsors.length === 0 ? (
                                    <p className="text-xs text-gray-400 text-center py-8">No sponsors yet — click "Add Sponsor" below.</p>
                                ) : (
                                    sponsors.map((s) => (
                                        <div key={s.id}>
                                            <div className="flex items-center justify-between px-3 py-2">
                                                <span className="text-sm text-gray-800 truncate flex-1">
                                                    {s.name || <span className="text-gray-400 italic">Unnamed sponsor</span>}
                                                </span>
                                                <div className="flex items-center gap-3 ml-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeSponsor(s.id)}
                                                        className="text-xs text-red-600 hover:underline"
                                                    >Delete</button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditingId(editingId === s.id ? null : s.id)}
                                                        className="text-xs text-[#358ECD] hover:underline"
                                                    >{editingId === s.id ? 'Close' : 'Edit'}</button>
                                                </div>
                                            </div>
                                            {editingId === s.id && (
                                                <div className="px-3 pb-3">
                                                    <SponsorEditForm
                                                        sponsor={s}
                                                        onChange={(next) => updateSponsor(s.id, next)}
                                                        onDone={() => setEditingId(null)}
                                                        onUpload={(dataUrl, suggestedName) => updateSponsor(s.id, {
                                                            ...s, logo: dataUrl,
                                                            name: s.name || suggestedName,
                                                        })}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="flex justify-end mt-3">
                                <button
                                    type="button"
                                    onClick={addSponsor}
                                    className="px-3 py-1.5 text-sm text-[#358ECD] border border-[#358ECD] rounded-lg hover:bg-[#E8F1F8] flex items-center gap-1.5"
                                >
                                    <Plus size={14} /> Add Sponsor
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: Live preview with drag-reorder */}
                    <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-[#4D4D4D] text-center">Preview</h3>
                        <p className="text-xs text-gray-500 text-center mb-3">Drag to reorder sponsor logos</p>
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-[#4D4D4D] mb-2">Support Our Project</h4>
                            {donateUrl && (
                                <a
                                    href={donateUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[#0866AB] text-sm font-medium underline mb-3 inline-block"
                                >Donate Now</a>
                            )}
                            {sponsors.length === 0 ? (
                                <p className="text-xs text-gray-400 text-center py-6">No sponsors yet.</p>
                            ) : (
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={sponsors.map((s) => s.id)} strategy={rectSortingStrategy}>
                                        <div className="grid grid-cols-2 gap-2 mt-2">
                                            {sponsors.map((s) => (
                                                <SortableLogoTile key={s.id} id={s.id} sponsor={s} />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
